/**
 * Resolve what an experiment is measured on.
 *
 * This chain was copy-pasted in five places — the statistics SQL builder, the
 * timeseries handler, the stats-regeneration cron and two dashboard hooks — each
 * spelling `eventGoals[0] ?? website.conversionEvent ?? 'purchase'` by hand. They
 * agreed by luck rather than construction, and the cron and the API each had to be
 * edited to change the fallback. One reader now owns it.
 *
 * A website maps which of its events may be used as an objective
 * (event_goals.role); an experiment picks from those pools. Roles are deliberately
 * not consulted here: this answers "what does this experiment measure", and an
 * event demoted to 'ignored' after an experiment already selected it must keep
 * reporting, or a finished experiment's numbers would change retroactively when
 * someone tidies the website's mapping.
 */

/**
 * Structural, not the full `Experiment` type: the cron selects three columns, the
 * handlers hold a hydrated row, and the dashboard holds the API's shape. All three
 * satisfy this.
 *
 * The json columns are `unknown` because that is what Drizzle infers for them.
 */
export type ObjectiveSource = {
  primaryGoal?: string | null;
  secondaryGoals?: unknown;
  eventGoals?: unknown;
  website?: { conversionEvent?: string | null } | null;
};

export type Objectives = {
  /** Always a name: the verdict has to be computed against something. */
  primary: string;
  /** Supporting metrics, in the operator's chosen order. Never includes `primary`. */
  secondaries: string[];
  /**
   * True when nothing was mapped anywhere and `primary` is the assumed default.
   *
   * The stats page names the objective in its column headers, and calling an
   * unconfigured website's column "Purchase" implies a mapping that does not
   * exist — so it keeps the generic "Conversions" label in that case. Exposed
   * here because the alternative is the caller re-deriving the fallback chain,
   * which is what this helper exists to prevent.
   */
  primaryIsDefault: boolean;
};

/**
 * What the pipeline measured before any of this was configurable, and still the
 * last resort. Matches the stored procedure's own default.
 */
export const DEFAULT_PRIMARY_OBJECTIVE = 'purchase';

const asName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toNames = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map(asName).filter((name): name is string => name !== null);
};

export function resolveObjectives(experiment: ObjectiveSource): Objectives {
  const legacyGoals = toNames(experiment.eventGoals);

  /**
   * `eventGoals[0]` sits between the new column and the website default on purpose.
   * The backfill copies it into primary_goal, but rows created between the deploy
   * and that script running have a null primary_goal, and dropping the old
   * convention here would silently move those experiments onto the website default
   * — a different event, so different numbers.
   */
  const mapped = asName(experiment.primaryGoal) ?? legacyGoals[0] ?? asName(experiment.website?.conversionEvent);
  const primary = mapped ?? DEFAULT_PRIMARY_OBJECTIVE;

  /**
   * No fallback to `eventGoals[1..]`. Nothing renders secondaries today, so there is
   * no behaviour to preserve, and a fallback would make "show none" impossible to
   * express: clearing the selection would resurrect the legacy list on the next read.
   */
  const secondaries = toNames(experiment.secondaryGoals).filter((name) => name !== primary);

  // An operator can select the same event twice across two pools; the report would
  // then render a duplicate block.
  return { primary, secondaries: [...new Set(secondaries)], primaryIsDefault: mapped === null };
}
